import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, Download, CheckCircle2, RefreshCw,
  UserCheck, UserX, Save, Trash2,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { InfoTip, inputCls, inputBaseCls, lockedInputCls, LockTip } from '../components/shared/FormKit';
import SortableHeader, { usePagedSortableData, TablePager } from '../components/SortableTable';
import { fetchJSON } from '../utils/api';
import { formatDate } from '../utils/dateFormat';
import { sectionTitle } from '../components/workflow/constants';

// A row whose hours are already imported cannot be re-matched.
const IMPORTED_HINT = 'Already imported — nothing left to change';
const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase text-left';

// Data is stored and overwritten per month, so the UI picks whole months and
// expands them to first/last day for the API.
const previousMonth = () => {
  const now = new Date();
  const d = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
};
const monthRangeToDates = (fromMonth, toMonth) => {
  const [ty, tm] = toMonth.split('-').map(Number);
  return {
    from: `${fromMonth}-01`,
    to: `${toMonth}-${String(new Date(ty, tm, 0).getDate()).padStart(2, '0')}`,
  };
};

const STATUS_BADGES = {
  unmatched: 'bg-red-50 text-red-700',
  ambiguous: 'bg-amber-50 text-amber-700',
  resolved: 'bg-green-50 text-green-700',
  ignored: 'bg-slate-100 text-slate-500',
};

const QMChargeableImport = () => {
  const { hasPermission } = useAuth();
  const canEdit = hasPermission('qm:timesheet_ops:edit');

  const [fromMonth, setFromMonth] = useState(previousMonth);
  const [toMonth, setToMonth] = useState(previousMonth);
  const [downloading, setDownloading] = useState(false);
  const [committing, setCommitting] = useState(false);
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);

  const [fileId, setFileId] = useState(null);
  const [file, setFile] = useState(null);
  const [matched, setMatched] = useState([]);
  const [unmatched, setUnmatched] = useState([]);

  const [staffOptions, setStaffOptions] = useState([]);
  const [picks, setPicks] = useState({});          // rawUserName → staffId string
  const [resolvingName, setResolvingName] = useState(null);

  const [aliases, setAliases] = useState([]);
  const [aliasBusy, setAliasBusy] = useState(null);

  const [retentionDays, setRetentionDays] = useState('');
  const [settingsSaving, setSettingsSaving] = useState(false);
  const [settingsMsg, setSettingsMsg] = useState(null);
  const [settingsErr, setSettingsErr] = useState(null);

  // Sortable + PAGE_SIZE-per-page for the three tables on this page.
  // Declared up here, above the early returns below, so the hook order is stable.
  const unmatchedTable = usePagedSortableData(unmatched);
  const matchedTable = usePagedSortableData(matched);
  const aliasTable = usePagedSortableData(aliases);

  const loadAliases = useCallback(async () => {
    try {
      const res = await fetchJSON('/api/qm/chargeable-import/aliases');
      setAliases(res.aliases || []);
    } catch (err) {
      setAliases([]);
    }
  }, []);

  useEffect(() => {
    if (!canEdit) return;
    fetchJSON('/api/qm/chargeable-import/settings')
      .then((res) => setRetentionDays(String(res.settings.retentionDays)))
      .catch((err) => setSettingsErr(err.message));
    fetchJSON('/api/qm/staff/options')
      .then((res) => setStaffOptions(res.staff || []))
      .catch(() => setStaffOptions([]));
    loadAliases();
  }, [canEdit, loadAliases]);

  const applyPreview = useCallback((res) => {
    setFileId(res.fileId);
    setFile(res.file || null);
    setMatched(res.matched || []);
    setUnmatched(res.unmatched || []);
  }, []);

  const handleDownload = async () => {
    if (fromMonth > toMonth) {
      setError('Start month must not be after end month.');
      return;
    }
    setDownloading(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetchJSON('/api/qm/chargeable-import/download', {
        method: 'POST',
        body: JSON.stringify(monthRangeToDates(fromMonth, toMonth)),
      });
      applyPreview(res);
      setPicks({});
      setMessage(`Downloaded and parsed ${res.file?.totalRows ?? 0} rows (${res.skippedRows ?? 0} leave/summary/zero rows skipped).`);
    } catch (err) {
      setError(err.message || 'Download failed.');
    } finally {
      setDownloading(false);
    }
  };

  const handleResolve = async (rawUserName, body) => {
    setResolvingName(rawUserName);
    setError(null);
    try {
      const res = await fetchJSON(`/api/qm/chargeable-import/${fileId}/resolve`, {
        method: 'POST',
        body: JSON.stringify({ rawUserName, ...body }),
      });
      applyPreview(res);
      loadAliases(); // the decision was remembered as a saved mapping
    } catch (err) {
      setError(err.message || 'Failed to resolve name.');
    } finally {
      setResolvingName(null);
    }
  };

  const handleCommit = async () => {
    setCommitting(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetchJSON(`/api/qm/chargeable-import/${fileId}/commit`, { method: 'POST' });
      applyPreview(res);
      setMessage(`Imported ${res.result.detailRows} detail rows across ${res.result.staffMonths} staff-months.`);
    } catch (err) {
      setError(err.message || 'Import failed.');
    } finally {
      setCommitting(false);
    }
  };

  const handleDeleteAlias = async (id) => {
    setAliasBusy(id);
    try {
      await fetchJSON(`/api/qm/chargeable-import/aliases/${id}`, { method: 'DELETE' });
      setAliases((list) => list.filter((a) => a.id !== id));
    } catch (err) {
      setError(err.message || 'Failed to delete saved mapping.');
    } finally {
      setAliasBusy(null);
    }
  };

  const handleSaveSettings = async () => {
    setSettingsSaving(true);
    setSettingsMsg(null);
    setSettingsErr(null);
    try {
      const res = await fetchJSON('/api/qm/chargeable-import/settings', {
        method: 'PUT',
        body: JSON.stringify({ retentionDays: Number(retentionDays) }),
      });
      setRetentionDays(String(res.settings.retentionDays));
      setSettingsMsg('Retention setting saved.');
    } catch (err) {
      setSettingsErr(err.message || 'Failed to save settings.');
    } finally {
      setSettingsSaving(false);
    }
  };

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need staff records editing rights to import chargeable hours.</p>
        </div>
      </div>
    );
  }

  // Rows resolved after the last import re-enable the button for a top-up.
  const pending = matched.some((m) => !m.committed);
  const pendingNames = unmatched.filter((u) => u.matchStatus === 'unmatched' || u.matchStatus === 'ambiguous').length;

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto">
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700"><ArrowLeft className="w-4 h-4" />Management</Link>
      <div className="mt-2 mb-6">
        <h1 className="text-xl sm:text-2xl font-bold text-slate-900 inline-flex items-center gap-2">
          Chargeable Hours Import
          <InfoTip text="Data is stored per month — re-downloading a month replaces it (latest import wins). Unrecognised names wait here until you assign or ignore them; your decisions are remembered as suggestions for the next download." />
        </h1>
        <p className="text-sm text-slate-500 mt-1">
          Download the TimesheetWithoutCost report from Adaptive for whole months, review the staff matching, then import.
        </p>
      </div>

      {/* Download */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">From Month</label>
            <input type="month" value={fromMonth} onChange={(e) => setFromMonth(e.target.value)} className={`${inputCls} sm:w-auto`} />
          </div>
          <div>
            <label className="block text-xs font-medium text-slate-500 mb-1">To Month</label>
            <input type="month" value={toMonth} onChange={(e) => setToMonth(e.target.value)} className={`${inputCls} sm:w-auto`} />
          </div>
          <button
            onClick={handleDownload}
            disabled={downloading || !fromMonth || !toMonth}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {downloading ? <RefreshCw className="w-4 h-4 animate-spin" /> : <Download className="w-4 h-4" />}
            {downloading ? 'Downloading…' : 'Download & Preview'}
          </button>
          {downloading && <span className="text-sm text-slate-500">Logging in to Adaptive and pulling the report — this can take a minute.</span>}
        </div>
        {message && (
          <div className="mt-3 text-sm text-green-600">{message}</div>
        )}
        {error && (
          <div className="mt-3 text-sm text-red-600">{error}</div>
        )}
      </div>

      {file && (
        <>
          {/* Batch summary */}
          <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5 mb-6">
            <div>
              <dl className="grid grid-cols-2 sm:grid-cols-5 gap-x-6 gap-y-2 text-sm">
                <div><dt className="text-slate-500">Range</dt><dd className="text-slate-900">{formatDate(file.fromDate)} → {formatDate(file.toDate)}</dd></div>
                <div><dt className="text-slate-500">Rows</dt><dd className="text-slate-900">{file.totalRows}</dd></div>
                <div><dt className="text-slate-500">Matched Names</dt><dd className="text-green-700 font-medium">{file.matchedNames}</dd></div>
                <div><dt className="text-slate-500">Unmatched Names</dt><dd className={file.unmatchedNames > 0 ? 'text-red-600 font-medium' : 'text-slate-900'}>{file.unmatchedNames}</dd></div>
                <div>
                  <dt className="text-slate-500">Status</dt>
                  <dd>
                    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${file.status === 'committed' ? 'bg-green-50 text-green-700' : 'bg-blue-50 text-blue-700'}`}>
                      {file.status}
                    </span>
                  </dd>
                </div>
              </dl>
            </div>
          </div>

          {/* Unmatched names */}
          {unmatched.length > 0 && (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6">
              <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
                <h2 className={sectionTitle}>
                  Names Needing Attention
                  <InfoTip text="These Excel names did not exactly match the staff master. Saved mappings from earlier batches are pre-selected — confirm with Assign, or pick someone else / Ignore. You can keep resolving and importing after an import (top-up)." />
                </h2>
              </div>
              <div className="table-scroll">
                <table className="min-w-full divide-y divide-slate-200 text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <SortableHeader label="Excel Name" sortKey="rawUserName" sortConfig={unmatchedTable.sortConfig} onSort={unmatchedTable.requestSort} className={THEAD_CLS} />
                      <SortableHeader label="Status" sortKey="matchStatus" sortConfig={unmatchedTable.sortConfig} onSort={unmatchedTable.requestSort} className={THEAD_CLS} />
                      <SortableHeader label="Hours" sortKey="totalHours" sortConfig={unmatchedTable.sortConfig} onSort={unmatchedTable.requestSort} className={THEAD_CLS} />
                      <SortableHeader label="Assign To" sortable={false} className={THEAD_CLS} />
                      <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-slate-100">
                    {unmatchedTable.rows.map((u) => {
                      const busy = resolvingName === u.rawUserName;
                      const locked = !!u.committed; // rows already imported — nothing left to change
                      const staffSuggestions = u.suggestions.filter((s) => s.staffId);
                      const ignoredBefore = u.suggestions.some((s) => s.action === 'ignore' && s.saved);
                      const pick = picks[u.rawUserName] ?? (staffSuggestions[0] ? String(staffSuggestions[0].staffId) : '');
                      const suggestionIds = new Set(staffSuggestions.map((s) => s.staffId));
                      return (
                        <tr key={u.rawUserName} className="hover:bg-slate-50">
                          <td className="px-4 py-2 text-slate-900 whitespace-nowrap">{u.rawUserName}</td>
                          <td className="px-4 py-2 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${STATUS_BADGES[u.matchStatus] || 'bg-slate-100 text-slate-600'}`}>
                              {u.matchStatus}{u.matchStatus === 'resolved' && u.staffName ? ` → ${u.staffName}` : ''}
                            </span>
                            {ignoredBefore && (
                              <span className="ml-1 inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">
                                ignored before
                              </span>
                            )}
                          </td>
                          <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{u.totalHours} ({u.rowCount} rows)</td>
                          <td className="px-4 py-2 min-w-[260px]">
                            <LockTip text={locked ? IMPORTED_HINT : null} block>
                              <select
                                value={pick}
                                onChange={(e) => setPicks((p) => ({ ...p, [u.rawUserName]: e.target.value }))}
                                className={locked ? lockedInputCls : inputCls}
                                disabled={locked}
                              >
                                <option value="">— Select staff —</option>
                                {staffSuggestions.length > 0 && (
                                  <optgroup label="Suggestions">
                                    {staffSuggestions.map((s) => (
                                      <option key={`s-${s.staffId}`} value={s.staffId}>
                                        {s.name} ({s.saved ? 'saved' : `${Math.round(s.score * 100)}%`})
                                      </option>
                                    ))}
                                  </optgroup>
                                )}
                                <optgroup label="All active staff">
                                  {staffOptions.filter((s) => !suggestionIds.has(s.id)).map((s) => (
                                    <option key={s.id} value={s.id}>{s.name}{s.designation ? ` (${s.designation})` : ''}</option>
                                  ))}
                                </optgroup>
                              </select>
                            </LockTip>
                          </td>
                          <td className="px-4 py-2 text-right whitespace-nowrap">
                            <LockTip text={locked ? IMPORTED_HINT : null} align="right">
                              <div className="inline-flex gap-2">
                                <button
                                  onClick={() => handleResolve(u.rawUserName, { staffId: Number(pick) })}
                                  disabled={busy || !pick || locked}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 bg-blue-600 text-white text-xs font-medium rounded-md hover:bg-blue-700 disabled:opacity-40"
                                >
                                  <UserCheck className="w-3.5 h-3.5" /> Assign
                                </button>
                                <button
                                  onClick={() => handleResolve(u.rawUserName, { action: 'ignore' })}
                                  disabled={busy || locked || u.matchStatus === 'ignored'}
                                  className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 text-slate-600 text-xs font-medium rounded-md hover:bg-slate-50 disabled:opacity-40"
                                >
                                  <UserX className="w-3.5 h-3.5" /> Ignore
                                </button>
                              </div>
                            </LockTip>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              <TablePager {...unmatchedTable} className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50" />
            </div>
          )}

          {/* Matched summary + import */}
          <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6">
            <div className="px-4 py-3 border-b border-slate-200 bg-slate-50 flex flex-wrap items-center justify-between gap-3">
              <div>
                <h2 className={sectionTitle}>
                  Ready to Import (Per Staff × Month)
                  <InfoTip text="Importing replaces any existing chargeable hours for each pending staff member and month below. Leave hours are shown for reference (used by Timesheet Check) and never count as chargeable." />
                </h2>
                {/* Unresolved names stay on the page — it tells the user rows will be
                    left out of this import, which is a reason to act, not background. */}
                {pendingNames > 0 && (
                  <p className="text-xs text-amber-700 mt-0.5">
                    {pendingNames} name(s) still unresolved — their rows are left out until assigned.
                  </p>
                )}
              </div>
              <button
                onClick={handleCommit}
                disabled={committing || !pending}
                className="inline-flex items-center gap-2 px-4 py-2 bg-green-600 text-white text-sm font-medium rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {committing ? <RefreshCw className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {committing ? 'Importing…' : pending ? 'Import' : file.status === 'committed' ? 'Imported' : 'Import'}
              </button>
            </div>
            <div className="table-scroll">
              <table className="min-w-full divide-y divide-slate-200 text-sm">
                <thead className="bg-slate-50">
                  <tr>
                    <SortableHeader label="Staff" sortKey="staffName" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="Month" sortKey="month" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="Audit Hours" sortKey="auditHours" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="Non-audit Hours" sortKey="nonAuditHours" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="Leave Hours" sortKey="leaveHours" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="Rows" sortKey="rowCount" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="Via" sortKey="matchStatus" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} className={THEAD_CLS} />
                    <SortableHeader label="State" sortKey="committed" sortConfig={matchedTable.sortConfig} onSort={matchedTable.requestSort} accessor={(m) => (m.committed ? 1 : 0)} className={THEAD_CLS} />
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-slate-100">
                  {matched.length === 0 ? (
                    <tr><td colSpan="8" className="px-4 py-8 text-center text-slate-500">No matched rows yet</td></tr>
                  ) : matchedTable.rows.map((m) => (
                    <tr key={`${m.staffId}-${m.month}-${m.matchStatus}-${m.committed}`} className="hover:bg-slate-50">
                      <td className="px-4 py-2 text-slate-900 whitespace-nowrap">{m.staffName}</td>
                      <td className="px-4 py-2 text-slate-700 whitespace-nowrap">{m.month}</td>
                      <td className="px-4 py-2 text-slate-700">{m.auditHours}</td>
                      <td className="px-4 py-2 text-slate-700">{m.nonAuditHours}</td>
                      <td className="px-4 py-2 text-slate-500">{m.leaveHours || 0}</td>
                      <td className="px-4 py-2 text-slate-500">{m.rowCount}</td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.matchStatus === 'resolved' ? 'bg-amber-50 text-amber-700' : 'bg-blue-50 text-blue-700'}`}>
                          {m.matchStatus === 'resolved' ? 'manual' : 'auto'}
                        </span>
                      </td>
                      <td className="px-4 py-2">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${m.committed ? 'bg-green-50 text-green-700' : 'bg-amber-50 text-amber-700'}`}>
                          {m.committed ? 'imported' : 'pending'}
                        </span>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <TablePager {...matchedTable} className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50" />
          </div>
        </>
      )}

      {/* Saved name mappings */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-slate-200 bg-slate-50">
          <h2 className={sectionTitle}>
            Saved Name Mappings
            <InfoTip text="Assign / Ignore decisions are remembered here and pre-selected as suggestions on the next download. Delete a mapping if it points to the wrong person." />
          </h2>
        </div>
        {aliases.length === 0 ? (
          <p className="px-4 py-6 text-sm text-slate-500 text-center">No saved mappings yet.</p>
        ) : (
          <div className="table-scroll">
            <table className="min-w-full divide-y divide-slate-200 text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <SortableHeader label="Excel Name" sortKey="rawName" sortConfig={aliasTable.sortConfig} onSort={aliasTable.requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Mapped To" sortKey="staffName" sortConfig={aliasTable.sortConfig} onSort={aliasTable.requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Updated" sortKey="updatedAt" sortConfig={aliasTable.sortConfig} onSort={aliasTable.requestSort} className={THEAD_CLS} />
                  <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-slate-100">
                {aliasTable.rows.map((a) => (
                  <tr key={a.id} className="hover:bg-slate-50">
                    <td className="px-4 py-2 text-slate-900 whitespace-nowrap">{a.rawName}</td>
                    <td className="px-4 py-2 whitespace-nowrap">
                      {a.action === 'ignore' ? (
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-slate-100 text-slate-500">Ignored</span>
                      ) : (
                        <span className="text-slate-700">{a.staffName || '—'}</span>
                      )}
                    </td>
                    <td className="px-4 py-2 text-slate-500 whitespace-nowrap">{a.updatedAt ? a.updatedAt.replace('T', ' ') : '—'}</td>
                    <td className="px-4 py-2 text-right">
                      <button
                        onClick={() => handleDeleteAlias(a.id)}
                        disabled={aliasBusy === a.id}
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded disabled:opacity-40"
                        title="Delete saved mapping"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            <TablePager {...aliasTable} className="flex items-center justify-between px-4 py-2 border-t border-slate-200 bg-slate-50" />
          </div>
        )}
      </div>

      {/* Retention settings */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-5">
        <h2 className={`${sectionTitle} mb-3`}>
          Excel Retention
          <InfoTip text="Downloaded Excel files older than this many days are deleted automatically on the next download (batch history is kept)." />
        </h2>
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="number"
            min="1"
            max="3650"
            value={retentionDays}
            onChange={(e) => setRetentionDays(e.target.value)}
            className={`${inputBaseCls} w-28`}
          />
          <span className="text-sm text-slate-600">days</span>
          <button
            onClick={handleSaveSettings}
            disabled={settingsSaving || !retentionDays}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            <Save className="w-4 h-4" /> {settingsSaving ? 'Saving…' : 'Save'}
          </button>
          {settingsMsg && (
            <span className="text-sm text-green-600">{settingsMsg}</span>
          )}
          {settingsErr && (
            <span className="text-sm text-red-600">{settingsErr}</span>
          )}
        </div>
      </div>
    </div>
  );
};

export default QMChargeableImport;
